import React from 'react';
import PropTypes from 'prop-types';

import {
  BannerStyled,
  IconStyled,
  ParagraphStyled,
  IconCloseStyled,
  BannerContent,
} from './Banner.styled';

export const bannerVariant = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  SUCCESS: 'success',
};

export const bannerSize = {
  XSMALL: 'xsmall',
  SMALL: 'small',
  DEFAULT: 'default',
  MEDIUM: 'medium',
};

export default function Banner(props) {
  const { children, text, customIcon, isFixed, isDismissable, richChildren } =
    props;
  return (
    <BannerStyled role='banner' isShorted={!text && !children} {...props}>
      <BannerContent isFixed={isFixed} isDismissable={isDismissable}>
        {!props.disableIcon && (
          <IconStyled
            name={customIcon || props.variant}
            isShorted={!text && !children}
          />
        )}
        {text && <ParagraphStyled size={props.size} text={text} />}
        {children && !text && !richChildren && (
          <ParagraphStyled size={props.size}>{children}</ParagraphStyled>
        )}
        {children && !text && richChildren && children}
      </BannerContent>
      {isDismissable && (
        <IconCloseStyled name='close' onClick={isDismissable} />
      )}
    </BannerStyled>
  );
}

Banner.defaultProps = {
  variant: bannerVariant.INFO,
  size: bannerSize.SMALL,
  disableIcon: false,
  isFixed: false,
  isInline: false,
};

Banner.propTypes = {
  children: PropTypes.node,
  /**
   * Text to show inside the banner. The content of the banner can be filled also with the children prop, if we want to show html content inside the banner
   */
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * Allows showing an <a href="https://storybook.wikifactory.com/?path=/docs/4--page">icon
   * present on the theme</a> by name. By default the icon shown is same as variant prop.
   */
  customIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /**
   * This prop remove any icon associated to the banner variant
   */
  disableIcon: PropTypes.bool,
  /**
   * The variant prop provides different appearances for the component.
   */
  variant: PropTypes.oneOf(Object.values(bannerVariant)),
  /**
   * If we need to show a different background color from our variants versions <code>Info, Succes, Warning, Error</code>, we can pass a new color in this prop.
   */
  customBgColor: PropTypes.string,
  /**
   * This prop allows to change the size of the component based on our <a href="https://storybook.wikifactory.com/?path=/docs/3--page">typography scale system</a>
   */
  size: PropTypes.oneOf(Object.values(bannerSize)),
  /**
    Position banner fitting the width into its parent container without spacing and border-radius
  */
  isFixed: PropTypes.bool,
  /**
    Showing the banner as an inline element. The width of the banner will be based on the width of the text.
  */
  isInline: PropTypes.bool,
  /*
    Children to Banner will not be <p> compatible descendants
  */
  richChildren: PropTypes.bool,
  /**
    This prop allows the component to be dismissable by a function, a cross Icon will be shown at the right for triggering it.
  */
  isDismissable: PropTypes.func,
};
